import { Route, Routes as ReactRouterRoutes, Navigate } from 'react-router-dom'

import { CategoryContainer } from '../category/CategoryContainer'
import { CategoryNonprofitsTable } from '../category/categoryNonprofitsTable/CategoryNonprofitsTable'
import { Dashboard } from '../dashboard/Dashboard'
import { AskImpactPage } from '../dashboard/AskImpact/AskImpactPage'
import { DonationContainer } from '../donation/DonationContainer'
import { CandidateDetailsContainer } from '../grants/candidateDetails/CandidateDetailsContainer'
import { CandidatesContainer } from '../grants/candidates/CandidatesContainer'
import { MyTasksContainer } from '../grants/myTasks/MyTasksContainer'
import { ProgramsContainer } from '../grants/programs/ProgramsContainer'
import { NonprofitsContainer } from '../nonprofits/NonprofitsContainer'
import { OrganisationsContainer } from '../organisations/OrganisationsContainer'
import { OrganisationProfileContainer } from '../organisations/OrganisationProfileContainer'
import { useCanViewOrganisations } from '../organisations/useCanViewOrganisations'
import { VerificationDetailsContainer } from '../verifications/VerificationDetailsContainer'
import { VerificationsContainer } from '../verifications/VerificatonsContainer'
import { EmployeesContainer } from '../workplaceGiving/Employees/EmployeesContainer'
import { FundraisersContainer } from '../workplaceGiving/Fundraisers/FundraisersContainer'
import { VolunteeringContainer } from '../workplaceGiving/Volunteering/VolunteeringContainer'
import { VolunteeringSessionsOpportunityContainer } from '../workplaceGiving/Volunteering/VolunteeringSessionsOpportunityContainer/VolunteeringSessionsOpportunityContainer'
import { TemplatesContainer } from '../workplaceGiving/Templates/TemplatesContainer'
import { SurveyTemplateForm } from '../workplaceGiving/Templates/SurveyTemplateForm/SurveyTemplateForm'
import { WaiverTemplateForm } from '../workplaceGiving/Templates/WaiverTemplateForm/WaiverTemplateForm'
import { BulkWalletWizard } from '../workplaceGiving/Employees/BulkWalletWizard/BulkWalletWizard'
import { Integrations } from '../workplaceGiving/Integrations/Integrations'
import { CampaignsHome } from '../workplaceGiving/Campaigns/Home'
import { GivingActionForm } from '../workplaceGiving/Campaigns/ActionsTab/GivingActionForm/GivingActionForm'
import { EmployeeDetailsPage } from '../workplaceGiving/Employees/EmployeeDetails/EmployeeDetailsPage'
import { Settings } from '../settings/Settings'
import { FundraiserDetailContainer } from '../workplaceGiving/Fundraisers/FundraiserDetails/FundraiserDetailsContainer'
import { CorporateDonationContainer } from '../donation/corporateDonation/CorporateDonationContainer'
import { ProductRoadmapPage } from '../workplaceGiving/ProductRoadmap/ProductRoadmapPage'
import { Reports } from '../reports/Reports'
import { ReportDetailContainer } from '../reports/ReportDetailContainer'
import { moduleHasReports } from '../reports/reportsConfig'
import { useCanViewGrantsInsightsReports } from '../reports/useCanViewGrantsInsightsReports'
import { useCanViewInsightsReports } from '../reports/useCanViewInsightsReports'

import { usePermissionCheck } from '@percent/partner-dashboard/common/hooks/useAllow/usePermissionCheck'
import { canViewWGSection } from '@percent/partner-dashboard/context/iam/wgPermissions'
import { useAuthState, usePostHogFeatureFlag, POSTHOG_FEATURE_FLAGS } from '@percent/partner-dashboard/common/hooks'
import { selectScopes } from '@percent/partner-dashboard/context/auth/authReducer/authReducer'
import { PartnerScopes } from '@percent/partner-dashboard/constants/scopes'

export enum RoutePath {
  DASHBOARD = '/dashboard',
  ASK_IMPACT = '/ask-impact',
  SETTINGS = '/settings',
  DONATIONS = '/donations',
  CORPORATE_DONATION = '/donations/corporate-donation',
  NONPROFITS = '/nonprofits',
  ORGANISATIONS = '/organisations',
  ORGANISATION = '/organisations/:organisationId',
  CATEGORIES = '/categories',
  CATEGORIES_ID = '/categories/:categoryId',
  VERIFICATIONS = '/verifications',
  VERIFICATIONS_ID = '/verifications/:verificationId',
  GRANT_TASKS = '/grants/my-tasks',
  GRANT_PROGRAMS = '/grants/programs',
  GRANT_PROGRAM = '/grants/programs/:id',
  GRANT_PROGRAM_CANDIDATES = '/grants/programs/:id/candidates',
  GRANT_PROGRAM_CANDIDATE = '/grants/programs/:id/candidates/:candidateId',
  GRANTS_REPORTS = '/grants/reports',
  GRANTS_REPORT_DETAIL = '/grants/reports/:slug',
  WORKPLACE_GIVING = '/workplace-giving',
  WORKPLACE_GIVING_INTEGRATIONS = '/workplace-giving/integrations',
  WORKPLACE_GIVING_EMPLOYEES = '/workplace-giving/employees',
  WORKPLACE_GIVING_EMPLOYEES_ID = '/workplace-giving/employees/:employeeId',
  WORKPLACE_GIVING_EMPLOYEES_BULK_WALLET_WIZARD = '/workplace-giving/employees/bulk-wallet-wizard',
  WORKPLACE_GIVING_FUNDRAISERS = '/workplace-giving/fundraisers',
  WORKPLACE_GIVING_FUNDRAISERS_ID = '/workplace-giving/fundraisers/:fundraiserId',
  WORKPLACE_GIVING_VOLUNTEERING = '/workplace-giving/volunteering',
  WORKPLACE_GIVING_VOLUNTEERING_ID = '/workplace-giving/volunteering/:seriesOpportunityId',
  WORKPLACE_GIVING_TEMPLATES = '/workplace-giving/templates',
  WORKPLACE_GIVING_TEMPLATES_WAIVER_NEW = '/workplace-giving/templates/waiver/new',
  WORKPLACE_GIVING_TEMPLATES_WAIVER_EDIT = '/workplace-giving/templates/waiver/:templateId',
  WORKPLACE_GIVING_TEMPLATES_SURVEY_NEW = '/workplace-giving/templates/survey/new',
  WORKPLACE_GIVING_TEMPLATES_SURVEY_EDIT = '/workplace-giving/templates/survey/:templateId',
  WORKPLACE_GIVING_CAMPAIGNS = '/workplace-giving/campaigns',
  WORKPLACE_GIVING_CAMPAIGNS_ACTION_NEW = '/workplace-giving/campaigns/actions/new',
  WORKPLACE_GIVING_CAMPAIGNS_ACTION_EDIT = '/workplace-giving/campaigns/actions/:actionId',
  WORKPLACE_GIVING_PRODUCT_ROADMAP = '/workplace-giving/product-roadmap',
  WORKPLACE_GIVING_REPORTS = '/workplace-giving/reports',
  WORKPLACE_GIVING_REPORT_DETAIL = '/workplace-giving/reports/:slug'
}

export function Routes() {
  const { userCan, arePermissionsResolved } = usePermissionCheck()
  const { partner } = useAuthState()
  const canViewInsightsReports = useCanViewInsightsReports()
  const canViewGrantsInsightsReports = useCanViewGrantsInsightsReports()
  const canViewOrganisations = useCanViewOrganisations()

  const hasWorkplaceGiving = partner?.scopes?.some(data => data.scopeName.includes('workplace_giving'))
  const hasFundraisers = partner?.scopes?.some(data => data.scopeName.includes('donation_link'))
  const hasVolunteering = partner?.scopes?.some(data => data.scopeName.includes('volunteering'))
  const hasDonationWallet = partner?.scopes?.some(data => data.scopeName.includes('donation_wallet'))
  const hasSlack = partner?.scopes?.some(data => data.scopeName.includes('slack'))
  const hasGivingCampaign = partner?.scopes?.some(data => data.scopeName.includes('giving_campaign'))

  const canViewWG = canViewWGSection(userCan)
  // Mirrors the nav gate in Layout.tsx: no /workplace-giving route may be reachable
  // by deep link unless the partner has the module and the role can see the section.
  // Roles without it (member, developer) have the menu hidden, so a route registered
  // without this check is an access control hole rather than a hidden shortcut.
  const canAccessWorkplaceGiving = Boolean(hasWorkplaceGiving) && canViewWG
  // The employee-donations report is backed by the `giving` insights domain, which
  // the API gates on the WorkplaceGiving scope (on top of PartnerInsights). Require
  // that scope here too so a PartnerInsights-only partner can't deep-link into a
  // report that would 403 — matching how the nav nests it under Workplace giving.
  const canViewWgReports = canAccessWorkplaceGiving && canViewInsightsReports && moduleHasReports('workplace-giving')
  // Mirrors the nav gate in Layout.tsx: the Grants section needs the module on the partner
  // as well as the role, so no /grants route is reachable by deep link without both.
  const canAccessGrants = Boolean(selectScopes(partner?.scopes)[PartnerScopes.Grants]) && userCan('viewGrants')
  // The Reviews report is backed by the `grants` insights domain, which the API gates on the
  // Grants scope on top of PartnerInsights, so a PartnerInsights-only partner cannot deep-link
  // into a report that would 403.
  const canViewGrantsReports = canAccessGrants && canViewGrantsInsightsReports && moduleHasReports('grants')
  const givingActionsFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.PD_GIVING_ACTIONS)
  const canManageGivingActions =
    canAccessWorkplaceGiving && hasGivingCampaign && userCan('manageGivingActions') && givingActionsFeatureFlag
  const canViewTemplates =
    canAccessWorkplaceGiving && hasVolunteering && (userCan('viewSurveyTemplates') || userCan('viewWaiverTemplates'))
  const insightsAiChatFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.PD_INSIGHTS_AI_CHAT)
  const canViewAskImpact =
    insightsAiChatFeatureFlag &&
    Boolean(selectScopes(partner?.scopes)[PartnerScopes.PartnerInsights]) &&
    userCan('viewPartnerInsights')

  return (
    <ReactRouterRoutes>
      <Route path={RoutePath.SETTINGS} element={<Settings />} />
      <Route path={RoutePath.DASHBOARD} element={<Dashboard />} />
      {canViewAskImpact && <Route path={RoutePath.ASK_IMPACT} element={<AskImpactPage />} />}

      {userCan('viewDonations') && <Route path={RoutePath.DONATIONS} element={<DonationContainer />} />}
      {userCan('viewDonations') && (
        <Route path={RoutePath.CORPORATE_DONATION} element={<CorporateDonationContainer />} />
      )}
      {userCan('viewNonprofits') && <Route path={RoutePath.NONPROFITS} element={<NonprofitsContainer />} />}
      {canViewOrganisations && <Route path={RoutePath.ORGANISATIONS} element={<OrganisationsContainer />} />}
      {canViewOrganisations && <Route path={RoutePath.ORGANISATION} element={<OrganisationProfileContainer />} />}
      {canAccessGrants && <Route path={RoutePath.GRANT_TASKS} element={<MyTasksContainer />} />}
      {canAccessGrants && userCan('viewGrantPrograms') && (
        <Route path={RoutePath.GRANT_PROGRAMS} element={<ProgramsContainer />} />
      )}
      {canAccessGrants && <Route path={RoutePath.GRANT_PROGRAM_CANDIDATES} element={<CandidatesContainer />} />}
      {canAccessGrants && <Route path={RoutePath.GRANT_PROGRAM_CANDIDATE} element={<CandidateDetailsContainer />} />}
      {userCan('viewCategories') && <Route path={RoutePath.CATEGORIES} element={<CategoryContainer />} />}
      {userCan('viewCategories') && <Route path={RoutePath.CATEGORIES_ID} element={<CategoryNonprofitsTable />} />}
      {userCan('viewVerifications') && <Route path={RoutePath.VERIFICATIONS} element={<VerificationsContainer />} />}
      {userCan('viewVerifications') && (
        <Route path={RoutePath.VERIFICATIONS_ID} element={<VerificationDetailsContainer />} />
      )}

      {canAccessWorkplaceGiving && hasSlack && (
        <Route path={RoutePath.WORKPLACE_GIVING_INTEGRATIONS} element={<Integrations />} />
      )}
      {canAccessWorkplaceGiving && (
        <Route path={RoutePath.WORKPLACE_GIVING_EMPLOYEES} element={<EmployeesContainer />} />
      )}
      {canAccessWorkplaceGiving && userCan('manageDonationWallet') && hasDonationWallet && (
        <Route path={RoutePath.WORKPLACE_GIVING_EMPLOYEES_BULK_WALLET_WIZARD} element={<BulkWalletWizard />} />
      )}
      {canAccessWorkplaceGiving && (
        <Route path={RoutePath.WORKPLACE_GIVING_EMPLOYEES_ID} element={<EmployeeDetailsPage />} />
      )}
      {canAccessWorkplaceGiving && hasFundraisers && userCan('viewFundraisers') && (
        <Route path={RoutePath.WORKPLACE_GIVING_FUNDRAISERS} element={<FundraisersContainer />} />
      )}
      {canAccessWorkplaceGiving && hasFundraisers && userCan('viewFundraisers') && (
        <Route path={RoutePath.WORKPLACE_GIVING_FUNDRAISERS_ID} element={<FundraiserDetailContainer />} />
      )}
      {canAccessWorkplaceGiving && hasVolunteering && userCan('viewOpportunities') && (
        <Route path={RoutePath.WORKPLACE_GIVING_VOLUNTEERING} element={<VolunteeringContainer />} />
      )}
      {canAccessWorkplaceGiving && hasVolunteering && userCan('viewOpportunities') && (
        <Route
          path={RoutePath.WORKPLACE_GIVING_VOLUNTEERING_ID}
          element={<VolunteeringSessionsOpportunityContainer />}
        />
      )}
      {canAccessWorkplaceGiving && hasGivingCampaign && userCan('viewGivingCampaigns') && (
        <Route path={RoutePath.WORKPLACE_GIVING_CAMPAIGNS} element={<CampaignsHome />} />
      )}
      {canManageGivingActions && (
        <Route path={RoutePath.WORKPLACE_GIVING_CAMPAIGNS_ACTION_NEW} element={<GivingActionForm />} />
      )}
      {canManageGivingActions && (
        <Route path={RoutePath.WORKPLACE_GIVING_CAMPAIGNS_ACTION_EDIT} element={<GivingActionForm />} />
      )}
      {canViewTemplates && <Route path={RoutePath.WORKPLACE_GIVING_TEMPLATES} element={<TemplatesContainer />} />}
      {canViewTemplates && userCan('manageWaiverTemplates') && (
        <Route path={RoutePath.WORKPLACE_GIVING_TEMPLATES_WAIVER_NEW} element={<WaiverTemplateForm />} />
      )}
      {canViewTemplates && userCan('manageWaiverTemplates') && (
        <Route path={RoutePath.WORKPLACE_GIVING_TEMPLATES_WAIVER_EDIT} element={<WaiverTemplateForm />} />
      )}
      {canViewTemplates && userCan('manageSurveyTemplates') && (
        <Route path={RoutePath.WORKPLACE_GIVING_TEMPLATES_SURVEY_NEW} element={<SurveyTemplateForm />} />
      )}
      {canViewTemplates && userCan('manageSurveyTemplates') && (
        <Route path={RoutePath.WORKPLACE_GIVING_TEMPLATES_SURVEY_EDIT} element={<SurveyTemplateForm />} />
      )}
      {canAccessWorkplaceGiving && (
        <Route path={RoutePath.WORKPLACE_GIVING_PRODUCT_ROADMAP} element={<ProductRoadmapPage />} />
      )}
      {canViewGrantsReports && (
        <>
          <Route path={RoutePath.GRANTS_REPORTS} element={<Reports module="grants" />} />
          <Route path={RoutePath.GRANTS_REPORT_DETAIL} element={<ReportDetailContainer module="grants" />} />
        </>
      )}
      {canViewWgReports && (
        <Route path={RoutePath.WORKPLACE_GIVING_REPORTS} element={<Reports module="workplace-giving" />} />
      )}
      {canViewWgReports && (
        <Route
          path={RoutePath.WORKPLACE_GIVING_REPORT_DETAIL}
          element={<ReportDetailContainer module="workplace-giving" />}
        />
      )}
      {canAccessWorkplaceGiving && (
        <Route path={RoutePath.WORKPLACE_GIVING} element={<Navigate to={RoutePath.WORKPLACE_GIVING_EMPLOYEES} />} />
      )}
      {/* Every permission-gated route above is simply absent until `userCan` can answer, and
          permissions resolve asynchronously after mount. Registering the catch-all before then
          would bounce a deep link — `/ask-impact`, `/donations`, anything gated — to the dashboard
          and *rewrite the URL*, so the visitor never arrives even when they're entitled. Wait for
          the permissions fetch to settle; until it does an unmatched path simply renders nothing. */}
      {arePermissionsResolved && <Route path="*" element={<Navigate replace to={RoutePath.DASHBOARD} />} />}
    </ReactRouterRoutes>
  )
}
